import { ApiListResponse } from '@monetize/types/common';
import {
  AppColumnDef,
  createColumnHelper,
} from '../../../../../components/Monetize/DataTableNew/appTable';
import React, { useMemo, useRef } from 'react';
import { useAtom } from 'jotai';
import { MBox, MText } from '../../../../../components/Monetize';
import DataTableActions, {
  ActionType,
} from '../../../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../../../components/Monetize/DataTableNew/DataTable';
import {
  ACTION_COLUMN_KEY,
  createActionColumn,
} from '../../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import {
  getColHeaderWithSize,
  textBodyTemplate,
} from '../../../../../components/Monetize/DataTableNew/tableUtilsNew';
import { tablePagerSelector } from '../../../../../store/global.store';
import { FilterStateKeys } from '../../../../../store/store.types';
import { ReferenceResource } from '../../../../../types';

const columnHelper = createColumnHelper<ReferenceResource>();

const getColumns = (
  renderRefs: React.RefObject<{
    actionsTemplate: (rowData: ReferenceResource) => React.ReactNode;
  }>,
): AppColumnDef<ReferenceResource, any>[] => [
  columnHelper.accessor('name', {
    ...getColHeaderWithSize('Name', 250),
    cell: textBodyTemplate(),
    enableSorting: false,
  }),
  columnHelper.display({
    id: 'description',
    ...getColHeaderWithSize('Description', 250),
    cell: ({ row }) => {
      return (
        <MBox>
          <MText lineClamp={1}>{row.original.description}</MText>
          <MText fontSize="xs">
            {row.original.resourceContent.storageObject?.filename}
          </MText>
        </MBox>
      );
    },
    enableSorting: false,
  }),
  createActionColumn<ReferenceResource>((rowData) =>
    renderRefs.current?.actionsTemplate(rowData),
  ),
];

interface ReferenceResourceProps {
  data?: ApiListResponse<ReferenceResource>;
  isLoading: boolean;
  onEdit: (data: ReferenceResource) => void;
  onDownload: (data: ReferenceResource) => void;
  onDelete: (data: ReferenceResource) => Promise<void>;
}

export const ReferenceResourceTable = ({
  data,
  isLoading,
  onEdit,
  onDownload,
  onDelete,
}: ReferenceResourceProps) => {
  const renderRefs = useRef({
    actionsTemplate: (_rowData: ReferenceResource): React.ReactNode => null,
  });

  const actionBodyTemplate = (data: ReferenceResource) => {
    const actions: ActionType[] = [
      {
        title: 'Edit',
        enabled: true,
        action: () => onEdit(data),
      },
      {
        title: 'Download',
        enabled: true,
        action: () => onDownload(data),
      },
      {
        title: 'Delete',
        enabled: true,
        action: () => onDelete(data),
        color: 'danger',
      },
    ];
    return <DataTableActions actions={actions} />;
  };

  renderRefs.current.actionsTemplate = actionBodyTemplate;

  const [pager, setPager] = useAtom(
    tablePagerSelector(FilterStateKeys.REFERENCE_RESOURCE_LIST),
  );

  const columns = useMemo(() => getColumns(renderRefs), []);

  if (data?.content?.length === 0) {
    return null;
  }

  return (
    <DataTable
      columns={columns}
      data={data?.content || []}
      isLoading={isLoading}
      enablePagination
      clientControlled
      pinnedRightColumns={[ACTION_COLUMN_KEY]}
      totalElements={data?.totalElements || 0}
      totalPages={data?.totalPages}
      pager={pager}
      setPager={setPager}
    />
  );
};
